import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useFormFlow from './useFormFlow';
import useFormFlowSse from './useFormFlowSse';
import FormFlowShell from './FormFlowShell';

/**
 * Route entry for the respondent-facing Form Flow page.
 *
 * Two ways to enter test mode, in priority order:
 *  1. {@code testMode} prop — wired when the route is {@code /form-flow-test/:formSessionId}.
 *  2. {@code ?test=true} query param — lets operators share a single staging URL and flip
 *     collections without changing routes.
 *
 * Also supports an embedded-render path where the caller provides
 * {@code formSessionId} as a prop — used by the admin-dashboard force-start /
 * test-panel modals so they render the flow shell directly inside the dashboard
 * instead of nesting it in an iframe.
 */
export default function FormFlowPage({
  testMode = false,
  formSessionId: formSessionIdProp,
} = {}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const formSessionId = formSessionIdProp ?? params.formSessionId;
  const effectiveTest = testMode || searchParams.get('test') === 'true';

  const flow = useFormFlow({ formSessionId, test: effectiveTest });

  const sseEnabled = flow.submitState === 'transitioning' && !flow.sseError;
  useFormFlowSse({
    enabled: sseEnabled,
    streamUrl: flow.streamUrl,
    onEvent: flow.onStreamEvent,
  });

  return (
    <FormFlowShell
      session={flow.session}
      answers={flow.answers}
      validation={flow.validation}
      loading={flow.loading}
      error={flow.error}
      submitState={flow.submitState}
      hasPendingUploadAnswers={flow.hasPendingUploadAnswers}
      completion={flow.completion}
      sseError={flow.sseError}
      gateState={flow.gateState}
      onRequestMagicLink={flow.requestMagicLink}
      onSignedIn={flow.onSignedIn}
      onChangeAnswer={flow.onChangeAnswer}
      onFieldBlur={flow.onFieldBlur}
      onUpload={flow.onUpload}
      onRemoveFile={flow.onRemoveFile}
      retryUpload={flow.retryUpload}
      autoSaveStatus={flow.autoSaveStatus}
      lastSavedAt={flow.lastSavedAt}
      savingDraft={flow.savingDraft}
      saveDraft={flow.saveDraft}
      onSubmit={flow.submit}
      onBack={flow.back}
      onRetryTransitioning={flow.retryTransitioning}
    />
  );
}
